/// <amd-module name="Artifacts/FeedSettings/components/AddPermissionPanel" />
import * as Res from "Artifacts/FeedSettings/Resources";
import { CenteredSpinner } from "Artifacts/Root/components/CenteredSpinner";
import { ErrorBannerLoader } from "Artifacts/Root/components/ErrorBannerLoader";
import { ComponentState } from "Artifacts/Root/Constants";
import { isLoading } from "Artifacts/Root/helpers/ComponentStateHelpers";
import { roleToLocaleString } from "Artifacts/Root/helpers/RoleHelpers";
import * as RootRes from "Artifacts/Root/Resources";
import { FeedPermission, FeedRole } from "Artifacts/Root/web-apis/contracts/Feed";
import * as React from "react";
import { useMemo, useState } from "react";
import { PeoplePickerProvider } from "VSS/Features/Identities/IdentityPickerProvider";
import { useComponentContext } from "VSS/Platform/Layout";
import { FormItem } from "VSSUI/FormItem";
import { IdentityPickerDropdown, IIdentity } from "VSSUI/IdentityPicker";
import { Panel } from "VSSUI/Panel";
import { RadioButton, RadioButtonGroup } from "VSSUI/RadioButton";

export interface AddPermissionPanelProps {
  onDismiss: () => void;
  onSave: (permission?: FeedPermission) => void;
  saveButtonComponentState: ComponentState;
  supportServicePrincipals: boolean;
}

const AddPermissionPanel: React.FC<AddPermissionPanelProps> = ({
  onDismiss,
  onSave,
  saveButtonComponentState,
  supportServicePrincipals
}) => {
  const { pageContext } = useComponentContext();
  const peoplePickerProvider = new PeoplePickerProvider(pageContext, {
    identityTypes: supportServicePrincipals ? ["user", "group", "servicePrincipal"] :  ["user", "group"] ,
    operationScopes: ["ims"],
    options: {
      MinResults: 5,
      MaxResults: 20,
    },
  });
  const [identity, setIdentity] = useState<IIdentity | undefined>();
  const [selectedRole, setSelectedRole] = useState<string>(() => FeedRole.Reader.toString());

  const feedPermission: FeedPermission | undefined = useMemo(() => {
    if (identity && selectedRole) {
      return {
        displayName: identity?.displayName || "",
        identityId: identity?.localId || "",
        role: Number(selectedRole) as FeedRole,
        identityDescriptor: null as any,
        isInheritedRole: false,
      };
    }
  }, [identity, selectedRole]);

  return (
    <Panel
      contentClassName="add-permission-panel"
      titleProps={{ text: Res.FeedSettings_Permissions_AddPermissionModal_Title }}
      description= { supportServicePrincipals ? Res.FeedSettings_Permissions_AddPermissionModal_Description : ""}
      onDismiss={onDismiss}
      overlayContent={
        isLoading(saveButtonComponentState) ? (
          <CenteredSpinner label={RootRes.Submitting} />
        ) : (
          undefined
        )
      }
      footerButtonProps={[
        { text: Res.FeedSettings_CancelButton_Label, onClick: onDismiss },
        {
          text: Res.FeedSettings_SaveButton_Label,
          primary: true,
          disabled: !feedPermission,
          onClick: () => onSave(feedPermission),
        },
      ]}
    >
      <ErrorBannerLoader state={saveButtonComponentState} noLoadingSpinner>
        <FormItem
          className="flex add-permission-identity-label required"
          label={ Res.FeedSettings_Permissions_Grid_UserGroup}
        >
          <IdentityPickerDropdown
            required={true}
            onChange={(identity) => {
              setIdentity(identity);
            }}
            pickerProvider={peoplePickerProvider}
            value={identity}
          />
        </FormItem>
        <FormItem className="flex">
          <RadioButtonGroup
            text={Res.FeedSettings_Permissions_Grid_Role}
            selectedButtonId={selectedRole}
            onSelect={setSelectedRole}
          >
            <RadioButton
              key={FeedRole.Administrator.toString()}
              id={FeedRole.Administrator.toString()}
              text={roleToLocaleString(FeedRole.Administrator)}
            />
            <RadioButton
              key={FeedRole.Contributor.toString()}
              id={FeedRole.Contributor.toString()}
              text={roleToLocaleString(FeedRole.Contributor)}
            />
            <RadioButton
              key={FeedRole.Collaborator.toString()}
              id={FeedRole.Collaborator.toString()}
              text={roleToLocaleString(FeedRole.Collaborator)}
            />
            <RadioButton
              key={FeedRole.Reader.toString()}
              id={FeedRole.Reader.toString()}
              text={roleToLocaleString(FeedRole.Reader)}
            />
          </RadioButtonGroup>
        </FormItem>
      </ErrorBannerLoader>
    </Panel>
  );
};

export { AddPermissionPanel };
